// Login — institutional auth gate screen (US-874 · R-6, Atlas Standard polish).
//
// Collects credentials and awaits `onLogin(username, password)` — the real
// session login of US-882 (useAuth → POST /auth/login/ through the useApi
// seam). Resolving false means bad credentials (401); a rejection means the
// server could not be reached — each gets its own message (R-5/R-8). No
// direct API call here (R-10). The visual frame (ADB rainbow stripe,
// digiASIA lockup, navy primary, square card) follows the DESIGN.md
// "Institutional Atlas" system.
import { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";

const Login = ({ onLogin }) => {
  const { tokens } = useTheme();
  const { spacing, shadows, rainbow } = tokens;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // idle | submitting | invalid (401) | unreachable (network/5xx)
  const [status, setStatus] = useState("idle");
  const submitting = status === "submitting";
  const error = status === "invalid";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setStatus("submitting");
    try {
      const ok = await onLogin?.(username, password);
      // true → App swaps to Dashboard and this screen unmounts; only the
      // failure needs local state.
      if (ok === false) setStatus("invalid");
    } catch {
      setStatus("unreachable");
    }
  };

  // Clear the error as soon as the user edits either field.
  const clearError = () => {
    if (status === "invalid" || status === "unreachable") setStatus("idle");
  };

  // Six crisp ADB brand bands, left → right.
  const rainbowStripe = rainbow
    .map((color, i) => `${color} ${(i / rainbow.length) * 100}% ${((i + 1) / rainbow.length) * 100}%`)
    .join(", ");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        p: spacing.md,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 400,
          boxShadow: shadows.card,
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{ height: 4, background: `linear-gradient(90deg, ${rainbowStripe})` }}
        />
        <CardContent sx={{ p: 4 }}>
          <Typography
            component="p"
            sx={{ fontSize: 20, fontWeight: 700, color: "primary.main", letterSpacing: "0.01em" }}
          >
            digi
            <Box component="span" sx={{ color: "secondary.main" }}>
              ASIA
            </Box>
            <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>
              {" · Insights"}
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Connectivity Mapping Tool
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Sign in
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Enter your credentials to continue.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Incorrect username or password. Please try again.
              </Alert>
            )}
            {status === "unreachable" && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Could not sign in — the server did not respond. Try again.
              </Alert>
            )}
            <TextField
              label="Username"
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
                clearError();
              }}
              error={error}
              autoComplete="username"
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                clearError();
              }}
              error={error}
              autoComplete="current-password"
              fullWidth
              margin="normal"
              required
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={18} color="inherit" /> : null
              }
              sx={{ mt: 2.5, fontWeight: 600 }}
            >
              {submitting ? "Signing in…" : "Log in"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
